import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Alert, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import LearnerNavbar from '../components/LearnerNavbar';
import '../styles/brand.css';

const LearnerGrades = () => {
  const { currentUser, userProfile } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('assignments');

  useEffect(() => {
    if (currentUser && userProfile?.role === 'learner') {
      loadGradesData();
    }
  }, [currentUser, userProfile]);

  const loadGradesData = async () => {
    try {
      setLoading(true);

      // Load modules for the learner's cohort
      let modulesData = [];
      if (userProfile?.cohortId) {
        const modulesQuery = query(
          collection(db, 'modules'),
          where('cohortIds', 'array-contains', userProfile.cohortId)
        );
        const modulesSnapshot = await getDocs(modulesQuery);
        modulesData = modulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } else {
        // If no cohort assigned, load all modules (fallback)
        const modulesSnapshot = await getDocs(collection(db, 'modules'));
        modulesData = modulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
      setModules(modulesData);

      // Load assignments
      const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
      const assignmentsData = assignmentsSnapshot.docs.map(doc => {
        const data = doc.data();
        const userSubmission = data.submissions?.[currentUser.uid];
        return {
          id: doc.id,
          ...data,
          userSubmission: userSubmission || null,
          type: 'assignment'
        };
      }).filter(assignment => {
        // Filter assignments for modules in learner's cohort
        return modulesData.some(module => module.id === assignment.moduleId);
      });
      setAssignments(assignmentsData);

      // Load assessments
      const assessmentsSnapshot = await getDocs(collection(db, 'assessments'));
      const assessmentsData = assessmentsSnapshot.docs.map(doc => {
        const data = doc.data();
        const userAttempts = data.attempts?.[currentUser.uid] || [];
        const bestAttempt = userAttempts.length > 0 ? 
          userAttempts.reduce((best, current) => 
            current.score > best.score ? current : best
          ) : null;
        return {
          id: doc.id,
          ...data,
          userAttempts,
          bestAttempt,
          type: 'assessment'
        };
      }).filter(assessment => {
        // Filter assessments for modules in learner's cohort
        return modulesData.some(module => module.id === assessment.moduleId);
      });
      setAssessments(assessmentsData);

    } catch (error) {
      devError('Error loading grades data:', error);
      setError('Failed to load grades data');
    } finally {
      setLoading(false);
    }
  };

  const getGradeLetter = (percentage) => {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  const getStatusBadge = (item) => {
    if (item.type === 'assignment') {
      if (!item.userSubmission) return <Badge bg="light" text="dark">Not Submitted</Badge>;
      if (item.userSubmission.status === 'submitted') return <Badge bg="warning">Pending Review</Badge>;
      if (item.userSubmission.status === 'graded') return <Badge bg="success">Graded</Badge>;
      return <Badge bg="secondary">Draft</Badge>;
    } else {
      if (!item.userAttempts || item.userAttempts.length === 0) return <Badge bg="light" text="dark">Not Attempted</Badge>;
      if (item.bestAttempt && item.bestAttempt.score >= item.passingScore) return <Badge bg="success">Passed</Badge>;
      return <Badge bg="danger">Failed</Badge>;
    }
  };

  const getScore = (item) => {
    if (item.type === 'assignment') {
      if (!item.userSubmission || item.userSubmission.score === null) return '-';
      return `${item.userSubmission.score}/${item.maxScore || 100}`;
    } else {
      if (!item.bestAttempt) return '-';
      return `${item.bestAttempt.score}%`;
    }
  };

  const getPercentage = (item) => {
    if (item.type === 'assignment') {
      if (!item.userSubmission || item.userSubmission.score === null) return null;
      return Math.round((item.userSubmission.score / (item.maxScore || 100)) * 100);
    } else {
      if (!item.bestAttempt) return null;
      return item.bestAttempt.score;
    }
  };

  const calculateOverallGrade = () => {
    const allItems = [...assignments, ...assessments];
    const gradedItems = allItems.filter(item => getPercentage(item) !== null);
    
    if (gradedItems.length === 0) return { percentage: 0, letter: 'N/A', total: 0 };
    
    const totalPoints = gradedItems.reduce((sum, item) => sum + getPercentage(item), 0);
    const percentage = Math.round(totalPoints / gradedItems.length);
    
    return {
      percentage,
      letter: getGradeLetter(percentage),
      total: gradedItems.length,
      totalItems: allItems.length
    };
  };

  const handleShowDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const getModuleName = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module ? module.title : 'Unknown Module';
  };

  const overallGrade = calculateOverallGrade();

  if (userProfile?.role !== 'learner') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>Only learners can view grades.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading your grades...</p>
        </div>
      </Container>
    );
  }

  return (
    <>
      <LearnerNavbar />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">My Grades</h1>
              <p className="mission-text mb-4">
                Track your academic progress and performance
              </p>
              <p className="mb-0">
                View your assignment scores, assessment results, and overall academic standing.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Overall Grade Summary */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h2 className="text-primary">{overallGrade.letter}</h2>
                <p className="mb-0">Overall Grade</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{overallGrade.percentage}%</h3>
                <p className="mb-0">Average Score</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{overallGrade.total}</h3>
                <p className="mb-0">Items Graded</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{overallGrade.totalItems - overallGrade.total}</h3>
                <p className="mb-0">Pending</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Grade Details Tabs */}
        <Card>
          <Card.Header>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Tab eventKey="assignments" title={`Assignments (${assignments.length})`}>
              </Tab>
              <Tab eventKey="assessments" title={`Assessments (${assessments.length})`}>
              </Tab>
            </Tabs>
          </Card.Header>
          <Card.Body>
            {activeTab === 'assignments' && (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Module</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Grade</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => {
                    const percentage = getPercentage(assignment);
                    return (
                      <tr key={assignment.id}>
                        <td>
                          <strong>{assignment.title}</strong>
                          <br />
                          <small className="text-muted">{assignment.description}</small>
                        </td>
                        <td>{getModuleName(assignment.moduleId)}</td>
                        <td>
                          {assignment.dueDate ? 
                            new Date(assignment.dueDate.toDate()).toLocaleDateString() : 
                            'No due date'
                          }
                        </td>
                        <td>{getStatusBadge(assignment)}</td>
                        <td>{getScore(assignment)}</td>
                        <td>
                          {percentage !== null ? (
                            <div>
                              <Badge bg={percentage >= 70 ? 'success' : 'danger'}>
                                {getGradeLetter(percentage)}
                              </Badge>
                              <div className="mt-1">
                                <ProgressBar 
                                  now={percentage} 
                                  variant={percentage >= 70 ? 'success' : 'danger'}
                                  style={{ height: '6px' }}
                                />
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowDetails(assignment)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {activeTab === 'assessments' && (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Assessment</th>
                    <th>Module</th>
                    <th>Attempts</th>
                    <th>Status</th>
                    <th>Best Score</th>
                    <th>Grade</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map((assessment) => {
                    const percentage = getPercentage(assessment);
                    return (
                      <tr key={assessment.id}>
                        <td>
                          <strong>{assessment.title}</strong>
                          <br />
                          <small className="text-muted">{assessment.description}</small>
                        </td>
                        <td>{getModuleName(assessment.moduleId)}</td>
                        <td>
                          {assessment.userAttempts.length} / {assessment.maxAttempts}
                        </td>
                        <td>{getStatusBadge(assessment)}</td>
                        <td>{getScore(assessment)}</td>
                        <td>
                          {percentage !== null ? (
                            <div>
                              <Badge bg={percentage >= assessment.passingScore ? 'success' : 'danger'}>
                                {getGradeLetter(percentage)}
                              </Badge>
                              <div className="mt-1">
                                <ProgressBar 
                                  now={percentage} 
                                  variant={percentage >= assessment.passingScore ? 'success' : 'danger'}
                                  style={{ height: '6px' }}
                                />
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowDetails(assessment)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {((activeTab === 'assignments' && assignments.length === 0) || 
              (activeTab === 'assessments' && assessments.length === 0)) && (
              <div className="text-center py-4">
                <p className="text-muted">
                  No {activeTab} found for your current course.
                </p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Details Modal */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedItem?.title} - Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedItem && (
              <div>
                <Row className="mb-4">
                  <Col md={6}>
                    <Card>
                      <Card.Header>
                        <h6>Basic Information</h6>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Title:</strong> {selectedItem.title}</p>
                        <p><strong>Module:</strong> {getModuleName(selectedItem.moduleId)}</p>
                        <p><strong>Type:</strong> {selectedItem.type}</p>
                        {selectedItem.dueDate && (
                          <p><strong>Due Date:</strong> {new Date(selectedItem.dueDate.toDate()).toLocaleString()}</p>
                        )}
                        <p><strong>Status:</strong> {getStatusBadge(selectedItem)}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card>
                      <Card.Header>
                        <h6>Grade Information</h6>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Score:</strong> {getScore(selectedItem)}</p>
                        {getPercentage(selectedItem) !== null && (
                          <>
                            <p><strong>Percentage:</strong> {getPercentage(selectedItem)}%</p>
                            <p><strong>Letter Grade:</strong> {getGradeLetter(getPercentage(selectedItem))}</p>
                          </>
                        )}
                        {selectedItem.type === 'assessment' && (
                          <p><strong>Passing Score:</strong> {selectedItem.passingScore}%</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Assignment-specific details */}
                {selectedItem.type === 'assignment' && selectedItem.userSubmission && (
                  <Card className="mb-3">
                    <Card.Header>
                      <h6>Submission Details</h6>
                    </Card.Header>
                    <Card.Body>
                      {selectedItem.userSubmission.submittedAt && (
                        <p><strong>Submitted:</strong> {new Date(selectedItem.userSubmission.submittedAt.toDate()).toLocaleString()}</p>
                      )}
                      
                      {selectedItem.userSubmission.files && selectedItem.userSubmission.files.length > 0 && (
                        <div>
                          <strong>Submitted Files:</strong>
                          <ul className="mt-2">
                            {selectedItem.userSubmission.files.map((file, index) => (
                              <li key={index}>
                                <a href={file.url} target="_blank" rel="noopener noreferrer">
                                  {file.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {selectedItem.userSubmission.feedback && (
                        <div className="mt-3">
                          <strong>Facilitator Feedback:</strong>
                          <div className="mt-2 p-3 bg-light rounded">
                            {selectedItem.userSubmission.feedback}
                          </div>
                        </div>
                      )}

                      {selectedItem.userSubmission.rubricScores && (
                        <div className="mt-3">
                          <strong>Rubric Scores:</strong>
                          <Table striped size="sm" className="mt-2">
                            <thead>
                              <tr>
                                <th>Criteria</th>
                                <th>Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(selectedItem.userSubmission.rubricScores).map(([criteria, score]) => (
                                <tr key={criteria}>
                                  <td>{criteria}</td>
                                  <td>{score} points</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {/* Assessment-specific details */}
                {selectedItem.type === 'assessment' && selectedItem.userAttempts.length > 0 && (
                  <Card className="mb-3">
                    <Card.Header>
                      <h6>Attempt History</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped size="sm">
                        <thead>
                          <tr>
                            <th>Attempt</th>
                            <th>Date</th>
                            <th>Score</th>
                            <th>Time Taken</th>
                            <th>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItem.userAttempts.map((attempt, index) => (
                            <tr key={index}>
                              <td>{attempt.attemptNumber}</td>
                              <td>{new Date(attempt.submittedAt).toLocaleString()}</td>
                              <td>{attempt.score}%</td>
                              <td>{Math.round(attempt.timeTaken / 60)} minutes</td>
                              <td>
                                <Badge bg={attempt.score >= selectedItem.passingScore ? 'success' : 'danger'}>
                                  {attempt.score >= selectedItem.passingScore ? 'Pass' : 'Fail'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default LearnerGrades;